/**
 * System layers: items of first-party source kinds that Studio itself keeps
 * in place. They are always visible, always locked, never removed, moved,
 * grouped or duplicated, and always drawn above every other item of their
 * scene (the last top-level item). The kinds are fixed here, not declared by
 * plugins, so a third-party plugin cannot claim the top of a scene.
 */
import type { Project, SceneItem } from './model';

/** `com.ohmypeer.organic-ads.audio`: the "OMP Advertising" source. */
export const SYSTEM_SOURCE_KINDS: ReadonlySet<string> = new Set(['com.ohmypeer.organic-ads.audio']);

/** The name system sources carry, and the old default it replaces (a name the user chose is kept). */
export const SYSTEM_SOURCE_NAME = 'OMP Advertising';
const OLD_SYSTEM_SOURCE_NAMES: ReadonlySet<string> = new Set(['', 'Organic ads audio']);

export const SYSTEM_ITEM_REFUSAL = 'OMP Advertising is a system layer: it always stays visible and on top, and cannot be hidden, unlocked, moved or removed.';

export function isSystemSourceKind(kind: string | undefined): boolean {
  return kind !== undefined && SYSTEM_SOURCE_KINDS.has(kind);
}

export function isSystemItem(project: Pick<Project, 'sources'>, item: SceneItem): boolean {
  return item.type === 'source' && isSystemSourceKind(project.sources[item.sourceId]?.kind);
}

/**
 * Restores the invariant in place (works on an immer draft or a plain
 * object): system sources still named with the old default are renamed
 * "OMP Advertising"; system items leave any group, sit at the end of the
 * scene's top-level list in their current relative order, visible and locked.
 * It writes nothing when the project already conforms, so a conforming
 * mutation records no extra patches.
 */
export function pinSystemItems(project: Pick<Project, 'sources' | 'scenes'>): void {
  // Projects saved before the rename still say "Organic ads audio" (or nothing): the system name replaces only that.
  for (const source of Object.values(project.sources)) {
    if (isSystemSourceKind(source.kind) && OLD_SYSTEM_SOURCE_NAMES.has(source.name.trim()) && source.name !== SYSTEM_SOURCE_NAME) {
      source.name = SYSTEM_SOURCE_NAME;
    }
  }
  for (const scene of Object.values(project.scenes)) {
    const pinned: SceneItem[] = [];
    const collect = (items: readonly SceneItem[]): void => {
      for (const item of items) {
        if (isSystemItem(project, item)) pinned.push(item);
        else if (item.type === 'group') collect(item.children);
      }
    };
    collect(scene.items);
    if (pinned.length === 0) continue;
    const tail = scene.items.slice(scene.items.length - pinned.length);
    const inPlace = tail.length === pinned.length && tail.every((item, i) => item === pinned[i]);
    if (!inPlace) {
      const ids = new Set(pinned.map((item) => item.id));
      const strip = (items: SceneItem[]): void => {
        for (let i = items.length - 1; i >= 0; i--) {
          const item = items[i]!;
          if (ids.has(item.id)) items.splice(i, 1);
          else if (item.type === 'group') strip(item.children);
        }
      };
      strip(scene.items);
      scene.items.push(...pinned);
    }
    for (const item of scene.items.slice(scene.items.length - pinned.length)) {
      if (!item.visible) item.visible = true;
      if (!item.locked) item.locked = true;
    }
  }
}
